import { Component, DestroyRef, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CompanyProductPhaseBoard } from 'src/app/models/company_product_phase_board.model';
import { ProductTask } from 'src/app/models/product_task.model';
import { ProductTaskOriginCategory } from 'src/app/models/product_task_origin_category.model';
import { ProductTaskOriginCategoryTypeAvailableEnum } from 'src/app/enums/product-task-origin-category-type-available.enum';
import { PRODUCT_TASK_STORY_TASK_MUST_BE_CREATED_IN_BACKLOG_TOOLTIP } from 'src/app/constants/product-task-product-trio-restrictions.constants';
import { ProductTaskTypeEnum } from 'src/app/enums/product-task-type.enum';
import { ProductTaskTypeFieldPipePipe } from 'src/app/pipes/product-task-type-field.pipe';
import { ProductTaskTypeDescriptionPipe } from 'src/app/pipes/product-task-type-description.pipe';
import { ProductTaskTypeImagePipe } from 'src/app/pipes/product-task-type-image.pipe';
import { ApiService, UtilsService } from 'src/app/services';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-create-product-task-dialog',
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltipModule,
    ProductTaskTypeFieldPipePipe,
    ProductTaskTypeDescriptionPipe,
    ProductTaskTypeImagePipe,
    MatInputModule,
    MatButtonModule,
  ],
  templateUrl: './create-product-task-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './create-product-task-dialog.component.css',
})
export class CreateProductTaskDialogComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<CreateProductTaskDialogComponent>>(MatDialogRef);
  private fb = inject(FormBuilder);
  private api = inject(ApiService);
  private utils = inject(UtilsService);
  private snack = inject(MatSnackBar);

  data = inject<{
    company_product_phase_id: number;
    title: string | undefined;
    type: string | undefined;
    company_product_phase_board_id: number;
    company_product_id: number;
    is_draft: boolean | undefined;
    order_company_product_phase: number | undefined;
    company_product_epic_id: number | undefined;
    is_hotfix?: boolean;
    needs_testing?: boolean;
    allowed_task_types?: ProductTaskTypeEnum[];
    force_draft?: boolean;
    board_only_issue_message?: string;
  }>(MAT_DIALOG_DATA);

  form: FormGroup;
  company_product_phase_board: CompanyProductPhaseBoard | undefined;
  company_product_phase_boards: CompanyProductPhaseBoard[] = [];
  loading: boolean = false;
  product_task_origin_categories: ProductTaskOriginCategory[] = [];
  filtered_origin_categories: ProductTaskOriginCategory[] = [];
  selected_origin_category_description: string = '';
  readonly taskTypes = Object.values(ProductTaskTypeEnum);
  readonly boardOnlyIssueMessage = PRODUCT_TASK_STORY_TASK_MUST_BE_CREATED_IN_BACKLOG_TOOLTIP;

  private destroy_ref = inject(DestroyRef);

  constructor() {
    this.form = this.fb.group({
      company_product_phase_id: [this.data.company_product_phase_id, Validators.compose([Validators.required])],
      title: [this.data.title, Validators.compose([Validators.required, Validators.minLength(5)])],
      type: [this.data.type, Validators.compose([Validators.required])],
      company_product_phase_board_id: [this.data.company_product_phase_board_id ?? '', Validators.compose([])],
      product_task_origin_category_id: [''],
      is_draft: [this.data.is_draft ?? false],
      order_company_product_phase: [this.data.order_company_product_phase],
      company_product_epic_id: [this.data.company_product_epic_id],
      is_hotfix: [this.data.is_hotfix ?? false],
      needs_testing: [this.data.needs_testing ?? false],
    });
  }

  ngOnInit() {
    this.checkInput();
    this.initTaskTypeRestrictions();
    this.initInputData();
    this.fetchProductTaskOriginCategories();
    this.fetchActiveCompanyProductPhaseBoards();
    this.listenTypeControl();
    this.listenOriginCategoryControl();
    this.listenBoardControl();
  }

  close() {
    this.dialogRef.close();
  }

  isTaskTypeDisabled(type: ProductTaskTypeEnum): boolean {
    const allowed_types = this.getEffectiveAllowedTaskTypes();

    return allowed_types != null && !allowed_types.includes(type);
  }

  isTaskTypeAllowed(type: ProductTaskTypeEnum): boolean {
    const allowed_types = this.getEffectiveAllowedTaskTypes();

    return allowed_types == null || allowed_types.includes(type);
  }

  submit() {
    const selectedBoardId = this.form.value.company_product_phase_board_id;
    if (selectedBoardId !== '' && selectedBoardId != null) {
      const selectedBoard = this.company_product_phase_boards.find(board => board.id == selectedBoardId);
      if (this.utils.isCompanyProductPhaseBoardPast(selectedBoard)) {
        this.snack.open('No se pueden crear tareas en un tablero pasado.', 'Ok', { duration: 5000 });
        return;
      }
    }

    this.loading = true;
    const body: any = { ...this.form.value };
    body.company_product_phase_id = this.data.company_product_phase_id;
    if (body.company_product_phase_board_id == '') delete body.company_product_phase_board_id;

    const is_on_board = body.company_product_phase_board_id != null;
    const allowed_types = this.getEffectiveAllowedTaskTypes();

    if (allowed_types != null && is_on_board) {
      body.type = ProductTaskTypeEnum.ISSUE;
      body.is_draft = false;
    } else if (this.data.force_draft) {
      body.is_draft = true;
    }

    // Mantenir order, draft i flags quan el caller els passa per no perdre'ls al JSON
    if (this.data.order_company_product_phase != null) body.order_company_product_phase = this.data.order_company_product_phase;
    if (this.data.is_draft !== undefined && body.is_draft === undefined) body.is_draft = this.data.is_draft;
    if (this.data.is_hotfix !== undefined) body.is_hotfix = this.data.is_hotfix;
    if (this.data.needs_testing !== undefined) body.needs_testing = this.data.needs_testing;

    this.api
      .createTask(body)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (data: ProductTask): void => this.dialogRef.close(data),
        error: (): boolean => (this.loading = false),
      });
  }

  private getEffectiveAllowedTaskTypes(): ProductTaskTypeEnum[] | undefined {
    const is_on_board = this.form.value.company_product_phase_board_id !== '' && this.form.value.company_product_phase_board_id != null;

    if (this.data.allowed_task_types != null) {
      return this.data.allowed_task_types;
    }

    if (this.data.force_draft && is_on_board) {
      return [ProductTaskTypeEnum.ISSUE];
    }

    return undefined;
  }

  private checkInput() {
    if (!this.data.company_product_phase_id) {
      console.error('You must provide company_product_phase_id.');
      this.dialogRef.close();
    }
    if (!this.data.company_product_id) {
      console.error('You must provide company_product_id.');
      this.dialogRef.close();
    }
  }

  private initTaskTypeRestrictions(): void {
    const allowed_types = this.getEffectiveAllowedTaskTypes();
    if (allowed_types != null && !this.isTaskTypeAllowed(this.form.value.type)) {
      this.form.controls['type'].patchValue(allowed_types[0]);
    }
  }

  private listenBoardControl(): void {
    this.form.controls['company_product_phase_board_id'].valueChanges.subscribe(() => {
      this.initTaskTypeRestrictions();
    });
  }

  private initInputData() {
    if (this.data.company_product_phase_board_id) {
      this.api.getCompanyProductPhaseBoard(this.data.company_product_phase_board_id).subscribe(data => {
        this.company_product_phase_board = data;
        if (!this.utils.isCompanyProductPhaseBoardPast(data)) {
          this.company_product_phase_boards = [data];
        }
      });
    }
  }

  private fetchActiveCompanyProductPhaseBoards() {
    this.api.getPhaseBoards(this.data.company_product_phase_id, { scopes: 'active' }).subscribe(data => {
      data = this.company_product_phase_board != null ? data.filter(board => board.id != this.company_product_phase_board!.id) : data;
      this.company_product_phase_boards = this.company_product_phase_boards.concat(data);
    });
  }

  private fetchProductTaskOriginCategories(): void {
    this.api
      .getProductTaskOriginCategories()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((data: ArrayResponse<ProductTaskOriginCategory>): void => {
        this.product_task_origin_categories = data.data;
        this.updateFilteredCategories(this.form.value.type);
      });
  }

  private updateFilteredCategories(type: string | null): void {
    this.filtered_origin_categories = this.product_task_origin_categories.filter(cat => this.categorySupportsType(cat, type));

    const currentValue = this.form.controls['product_task_origin_category_id'].value;
    const isStillValid = this.filtered_origin_categories.some(cat => cat.id === currentValue);
    if (!isStillValid) {
      this.form.controls['product_task_origin_category_id'].patchValue('');
    }

    this.updateOriginCategoryRequirement();
  }

  private listenTypeControl() {
    this.form.controls['type'].valueChanges.subscribe(data => {
      this.updateFilteredCategories(data);
      this.form.updateValueAndValidity();
    });
  }

  private listenOriginCategoryControl(): void {
    this.form.controls['product_task_origin_category_id'].valueChanges.subscribe(() => {
      this.updateSelectedOriginCategoryDescription();
    });
  }

  private updateOriginCategoryRequirement() {
    const ctrl = this.form.controls['product_task_origin_category_id'];
    const selectedType = this.form.controls['type'].value as string | null;
    const hasOriginCategoriesForType = this.product_task_origin_categories.some(category =>
      this.categorySupportsType(category, selectedType)
    );

    if (hasOriginCategoriesForType) {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
    }

    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private categorySupportsType(category: ProductTaskOriginCategory, type: string | null): boolean {
    if (type === null || category.types_available === null) {
      return false;
    }

    return category.types_available.includes(type as ProductTaskOriginCategoryTypeAvailableEnum);
  }

  private updateSelectedOriginCategoryDescription(): void {
    const selected_category_id = this.form.controls['product_task_origin_category_id'].value;
    const selected_category = this.filtered_origin_categories.find(category => category.id === selected_category_id);
    this.selected_origin_category_description = selected_category?.description ?? '';
  }
}
